/**
 * Hook: useAuthForm
 * 
 * Manages sign-in form state and submission
 * Handles email/password validation and auth errors
 * 
 * @returns {Object} { email, password, setEmail, setPassword, isSubmitting, error, message, handleSignIn, clear }
 */

import { useState } from 'react'
import { supabase } from '../supabaseClient'

export const useAuthForm = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const clearFeedback = () => {
    setError('')
    setMessage('')
  }

  const handleSignIn = async (event) => {
    if (event) {
      event.preventDefault()
    }

    clearFeedback()

    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedEmail || !password) {
      setError('Email and password are required.')
      return
    }

    setIsSubmitting(true)

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password
      })

      if (signInError) {
        setError(signInError.message || 'Unable to sign in.')
        return
      }

      if (!data?.session) {
        setError('Sign-in did not complete. No session was returned.')
        return
      }

      setPassword('')

      return data.session
    } catch (err) {
      setError(err?.message || 'Sign in failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const clear = () => {
    setEmail('')
    setPassword('')
    clearFeedback()
  }

  return {
    email,
    password,
    setEmail,
    setPassword,
    isSubmitting,
    error,
    message,
    handleSignIn,
    clearFeedback,
    clear
  }
}
